import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, SwitchCamera } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { FacingMode } from '@/types';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { uploadFile } from '@/services/supabaseStorage';
import { runOcr } from '@/lib/api';

interface ShippingLabelScannerProps {
  onCapture: (result: {
    imageBlob: Blob,
    ocrText?: string
  }) => Promise<void>;
  onCancel: () => void;
}

const CAMERA_CONSTRAINTS = {
  width: { ideal: 1280 },
  height: { ideal: 720 }
};

export const ShippingLabelScanner: React.FC<ShippingLabelScannerProps> = ({
  onCapture,
  onCancel,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<FacingMode>('environment');
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile] = useState(() => {
    const userAgent = navigator.userAgent || navigator.vendor;
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i
      .test(userAgent.toLowerCase());
  });
  const [processingCapture, setProcessingCapture] = useState(false);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      setIsInitialized(false);
    }
  }, []);

  const startCamera = useCallback(async () => {
    try {
      setError(null);

      // Only stop if we have an existing stream
      if (streamRef.current) {
        stopCamera();
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { ...CAMERA_CONSTRAINTS, facingMode }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await new Promise<void>((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = () => resolve();
          }
        });
        await videoRef.current.play();
      }

      streamRef.current = mediaStream;
      setIsInitialized(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to access camera';
      setError(errorMessage);
      toast.error(`Camera error: ${errorMessage}`);
      setIsInitialized(false);
    }
  }, [facingMode, stopCamera]);

  useEffect(() => {
    let mounted = true;

    const initCamera = async () => {
      if (mounted) {
        await startCamera();
      }
    };

    initCamera();

    return () => {
      mounted = false;
      stopCamera();
    };
  }, [facingMode, startCamera, stopCamera]);

  const captureImage = useCallback(async () => {
    if (!videoRef.current || !isInitialized) return;

    try {
      setProcessingCapture(true);
      const canvas = document.createElement('canvas');
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Failed to get canvas context');
      }
      if (facingMode === 'user') {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }
      context.drawImage(video, 0, 0);
      // If mobile upload issues persist, consider resizing/compressing the image here before upload
      const blob = await new Promise<Blob | null>((resolve) => 
        canvas.toBlob(resolve, 'image/jpeg', 0.9)
      );
      if (!blob) {
        throw new Error('Failed to create image blob');
      }
      // Immediately process the image: upload, OCR, extract fields
      let ocrText = '';
      try {
        const file = new File([blob], 'label.jpg', { type: blob.type });
        const { url: imageUrl, error: uploadError } = await uploadFile({
            bucket: 'labels',
            path: `scan_${Date.now()}.jpg`,
            file,
        });

        if (uploadError) {
            throw new Error(`Upload failed: ${uploadError}`);
        }

        const ocrResult = await runOcr(imageUrl);
        ocrText = ocrResult.text;
      } catch (err) {
        toast.error('Failed to process image: ' + (err instanceof Error ? err.message : err));
      }
      await onCapture({ imageBlob: blob, ocrText });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to capture image';
      toast.error(message);
    } finally {
      setProcessingCapture(false);
    }
  }, [videoRef, isInitialized, facingMode, onCapture]);

  const toggleCamera = useCallback(() => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  }, []);

  const content = (
    <div className="relative">
      {error ? (
        <div className="p-4 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <Button 
            variant="outline" 
            onClick={startCamera}
            className="mt-2"
          >
            Retry Camera
          </Button>
        </div>
      ) : (
        <>
          <div className="relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-64 object-cover rounded-lg"
            />
            <Button 
              variant="outline" 
              onClick={toggleCamera}
              className="absolute top-2 right-2"
            >
              <SwitchCamera className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="mt-4 flex justify-center">
            <Button 
              onClick={captureImage} 
              disabled={!isInitialized || processingCapture}
              className="w-full"
            >
              {processingCapture ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Camera className="mr-2 h-4 w-4" />
                  Capture
                </>
              )}
            </Button>
          </div>
          
          {isMobile && (
            <div className="mt-2 text-center text-xs text-muted-foreground">
              <p>Tip: Hold your phone steady and ensure the label is well-lit</p>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <ErrorBoundary>
      {content}
    </ErrorBoundary>
  );
};
